import React from "react";
import SiteFooter from "@/components/SiteFooter";
import { PsyGestionIcon } from "@/components/PsyGestionLogo";

const C = {
  navy: "#1B3A5C",
  teal: "#2A9D8F",
  muted: "#6B85A3",
  border: "#D6E4F0",
  white: "#FFFFFF",
};

const sections = [
  {
    title: "1. Responsable du traitement",
    content: (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <p><strong>Nom commercial :</strong> PsyGestion — Freedom Architect</p>
        <p><strong>Email :</strong> <a href="mailto:contact@freedomarchitect.io" style={{ color: C.teal }}>contact@freedomarchitect.io</a></p>
        <p><strong>Délégué à la protection des données (DPO) :</strong> contact@freedomarchitect.io</p>
      </div>
    ),
  },
  {
    title: "2. Données collectées",
    content:
      "Nous collectons uniquement les données que vous saisissez volontairement : adresse email, recettes professionnelles, charges, paramètres de profil (statut fiscal, ancienneté, tarif par séance). Aucune donnée bancaire directe n'est collectée — les paiements sont traités exclusivement par Stripe Payments Europe Ltd.",
  },
  {
    title: "3. Finalité du traitement",
    content:
      "Vos données sont utilisées exclusivement pour : (a) calculer vos estimations fiscales et afficher votre tableau de bord personnalisé, (b) gérer votre abonnement et votre authentification, (c) vous envoyer des rappels d'échéances URSSAF si vous l'avez activé. Elles ne sont jamais vendues, partagées avec des tiers à des fins publicitaires, ni utilisées pour entraîner des modèles d'intelligence artificielle publics ou tiers.",
  },
  {
    title: "4. Base légale du traitement",
    content:
      "Le traitement de vos données repose sur : (a) l'exécution du contrat (CGU) pour les fonctionnalités de base, (b) votre consentement pour les rappels d'échéances par email, (c) l'intérêt légitime de l'éditeur pour la prévention des abus et la sécurité du service.",
  },
  {
    title: "5. Hébergement, sécurité et chiffrement",
    content: (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <p>
          Vos données sont hébergées sur une infrastructure cloud européenne (<strong>AWS eu-west-1, Irlande</strong>)
          via Supabase Inc. Les mesures de sécurité en place incluent :
        </p>
        <ul style={{ paddingLeft: 20, margin: 0 }}>
          <li>
            <strong>Chiffrement au repos : AES-256</strong> — toutes les données stockées en base sont chiffrées
            avec l'algorithme AES-256 au niveau du disque (chiffrement PostgreSQL natif Supabase).
          </li>
          <li>
            <strong>Chiffrement en transit : HTTPS/TLS 1.3</strong> — toutes les communications entre votre
            navigateur et nos serveurs sont chiffrées.
          </li>
          <li>
            <strong>Row Level Security (RLS) PostgreSQL</strong> — chaque ligne de données est verrouillée au
            niveau de la base par votre identifiant unique (<code>auth.uid()</code>). Il est techniquement
            impossible pour un autre utilisateur d'accéder à vos données, même avec un token valide.
          </li>
          <li>
            <strong>Isolation totale des données</strong> — vos recettes, charges et données de profil ne sont
            jamais exposées à d'autres utilisateurs, ni dans les logs, ni dans les API.
          </li>
          <li>
            <strong>Authentification JWT</strong> — tokens sécurisés avec expiration automatique et rotation.
            Aucun mot de passe stocké en clair (hachage bcrypt via Supabase Auth).
          </li>
          <li>
            <strong>Algorithmes protégés côté serveur</strong> — les formules de calcul fiscal sont exécutées
            dans des Edge Functions isolées (Deno), jamais exposées dans le code client.
          </li>
        </ul>
        <p style={{ fontStyle: "italic", fontSize: 13 }}>
          Pour plus de détails sur notre architecture de sécurité :{" "}
          <a href="/securite" style={{ color: C.teal }}>Page Sécurité</a>.
        </p>
      </div>
    ),
  },
  {
    title: "6. Assistant IA et confidentialité",
    content:
      "L'assistant fiscal IA traite vos messages en temps réel via un workflow sécurisé. Les conversations ne sont pas stockées de manière permanente et ne sont pas utilisées pour entraîner des modèles d'IA publics. L'assistant n'a accès qu'aux données de votre profil (CA, charges, statut fiscal) et aux messages que vous lui envoyez dans la session en cours. Nous vous recommandons de ne jamais saisir le nom, les données de santé ou les informations personnelles de vos patients.",
  },
  {
    title: "7. Sous-traitants",
    content: (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <p>Les sous-traitants suivants participent au traitement de vos données :</p>
        <ul style={{ paddingLeft: 20, margin: 0 }}>
          <li><strong>Supabase Inc.</strong> — Base de données et authentification (AWS eu-west-1, UE)</li>
          <li><strong>Cloudflare Inc.</strong> — Hébergement frontend et CDN</li>
          <li><strong>Stripe Payments Europe Ltd.</strong> — Traitement des paiements (Irlande, UE)</li>
        </ul>
        <p>Aucun sous-traitant n'est autorisé à utiliser vos données à des fins autres que celles définies dans la présente politique.</p>
      </div>
    ),
  },
  {
    title: "8. Durée de conservation",
    content:
      "Vos données sont conservées tant que votre compte est actif. En cas de suppression de compte, toutes vos données personnelles sont supprimées dans un délai de 30 jours. Les données de facturation sont conservées pendant la durée légale obligatoire (10 ans, conformément au Code de commerce).",
  },
  {
    title: "9. Vos droits RGPD",
    content: (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <p>Conformément au RGPD (Règlement UE 2016/679) et à la loi Informatique et Libertés, vous disposez des droits suivants :</p>
        <ul style={{ paddingLeft: 20, margin: 0 }}>
          <li><strong>Droit d'accès :</strong> obtenir une copie de toutes vos données personnelles</li>
          <li><strong>Droit de rectification :</strong> corriger des données inexactes</li>
          <li><strong>Droit à l'effacement :</strong> supprimer votre compte et toutes vos données</li>
          <li><strong>Droit à la portabilité :</strong> exporter vos données dans un format structuré (JSON)</li>
          <li><strong>Droit à la limitation :</strong> restreindre le traitement de vos données</li>
          <li><strong>Droit d'opposition :</strong> vous opposer au traitement pour motif légitime</li>
        </ul>
        <p>Pour exercer ces droits : <a href="mailto:contact@freedomarchitect.io" style={{ color: C.teal }}>contact@freedomarchitect.io</a>. Délai de réponse : 30 jours maximum. En cas de réclamation non résolue, vous pouvez saisir la CNIL (Commission Nationale de l'Informatique et des Libertés).</p>
      </div>
    ),
  },
  {
    title: "10. Cookies",
    content:
      "PsyGestion utilise uniquement des cookies techniques strictement nécessaires au fonctionnement de l'authentification et à la mémorisation de vos préférences d'interface. Aucun cookie publicitaire, aucun traceur tiers, aucun pixel de suivi n'est utilisé. Ces cookies techniques ne nécessitent pas votre consentement au titre de l'article 82 de la loi Informatique et Libertés.",
  },
  {
    title: "11. Transferts internationaux",
    content:
      "Vos données sont hébergées au sein de l'Union européenne (AWS Irlande). Certains sous-traitants (Cloudflare, Supabase) ont leur siège aux États-Unis mais assurent la conformité via les Clauses Contractuelles Types (CCT) approuvées par la Commission européenne.",
  },
];

export default function Confidentialite() {
  return (
    <div style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif", backgroundColor: C.white, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <nav style={{ backgroundColor: C.white, borderBottom: `1px solid ${C.border}`, padding: "16px 20px" }}>
        <div style={{ maxWidth: 680, margin: "0 auto", display: "flex", alignItems: "center", gap: 8 }}>
          <a href="/" style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
            <PsyGestionIcon size={28} />
            <span style={{ fontWeight: 700, fontSize: 16, color: C.navy }}>PsyGestion</span>
          </a>
        </div>
      </nav>

      <main style={{ flex: 1, padding: "64px 20px" }}>
        <div style={{ maxWidth: 680, margin: "0 auto" }}>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: C.navy, marginBottom: 8 }}>
            Politique de confidentialité
          </h1>
          <p style={{ fontSize: 13, color: C.muted, marginBottom: 48 }}>
            Dernière mise à jour : mars 2026
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
            {sections.map((s) => (
              <div key={s.title}>
                <h2 style={{ fontSize: 17, fontWeight: 700, color: C.navy, marginBottom: 10 }}>
                  {s.title}
                </h2>
                <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.8 }}>
                  {typeof s.content === "string" ? <p>{s.content}</p> : s.content}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
